/*************************************
 * Slider Initialization (with Autoplay, Infinite Loop, and Touch Gestures)
 *************************************/

/*
  This script sets up carousels that will cycle through items using autoplay, infinite loop, and touch gestures. 
  However, it won't physically move items or use transitions between items. It only updates the active state.
*/

class Slider {
  constructor(baseClassName, carouselIndex, infiniteLoop = true, transitionDuration = 500, autoplayIntervalDuration = null) {
    this.baseClassName = baseClassName;
    this.carouselIndex = carouselIndex;
    this.infiniteLoop = infiniteLoop;
    this.transitionDuration = transitionDuration;
    this.autoplayIntervalDuration = autoplayIntervalDuration;
    this.autoplayStandardIntervalDuration = autoplayIntervalDuration;
    this.autoplay = autoplayIntervalDuration !== null;
    this.selector = `.${this.baseClassName}__slider[data-carousel-index="${this.carouselIndex}"]`;
    this.carousel = document.querySelector(this.selector);
    this.progressBar = this.carousel.querySelector(`.${this.baseClassName}__progress-bar`);
    this.carouselItems = [];
    this.numItems = 0;
    this.currentSlideIndex = 1;
    this.lastWindowWidth = window.innerWidth;
    this.autoplayInterval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isSwiping = false;
    this.activeTimeout = null;
    this.init();
  }

  init() {
    if (!this.carousel) {
      console.error(`Carousel not found for index ${this.carouselIndex}`);
      return;
    }
    this.carouselItems = Array.from(this.carousel.querySelectorAll(`.${this.baseClassName}__item`));
    if (this.carouselItems.length < 1) {
      return; // Exit initialization if there are 1 or fewer items
    }
    this.setupCarouselItems();
    this.setupNavigationArrows();
    this.bindEvents();
    this.updateCarousel('next'); // Initial update to set the correct state
    if (this.autoplay) {
      this.setupAutoplay();
    }
  }

  setupCarouselItems() {
    this.numItems = this.carouselItems.length;
  }

  setupNavigationArrows() {
    this.prevButton = this.carousel.querySelector(`.${this.baseClassName}__prev`);
    this.nextButton = this.carousel.querySelector(`.${this.baseClassName}__next`);
  
    this.handlePrevButtonClick = (e) => {
      e.preventDefault();
      const slideNumber = this.currentSlideIndex - 1;
      this.carousel.style.setProperty('--move', '-1'); // Set move to -1 for previous button
      this.navigateToSlide(slideNumber, 'prev'); // Pass 'prev' direction
    };
  
    this.handleNextButtonClick = (e) => {
      e.preventDefault();
      const slideNumber = this.currentSlideIndex + 1;
      this.carousel.style.setProperty('--move', '1'); // Set move to 1 for next button
      this.navigateToSlide(slideNumber, 'next'); // Pass 'next' direction
    };
  }

  setupAutoplay() {
    if (this.autoplay) {
      this.resetTimerBar(); // Reset the timer bar before starting
      this.progressBar.style.width = '100%'; // Start the timer animation
  
      this.autoplayInterval = setInterval(() => {
        this.resetTimerBar(); // Reset the timer for the next slide
        this.progressBar.style.width = '100%'; // Animate the bar again
        const slideNumber = this.currentSlideIndex + 1;
        this.navigateToSlide(slideNumber, 'next');
      }, this.autoplayIntervalDuration);
    }
  }  

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevButtonClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextButtonClick);
    }

    this.handleTouchStart = (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.isSwiping = false;
    };

    this.handleTouchMove = (e) => {
      const touchCurrentX = e.touches[0].clientX;
      const deltaX = touchCurrentX - this.touchStartX;

      // Detect horizontal swiping
      if (!this.isSwiping && Math.abs(deltaX) > 20) {
        this.isSwiping = true;
      }

      if (this.isSwiping) {
        e.preventDefault(); // Prevent scrolling
        this.touchEndX = touchCurrentX;
      }
    };

    this.handleTouchEnd = () => {
      if (this.isSwiping) {
        const sensitivity = 50; // Minimum distance to trigger a swipe
        if (this.touchStartX - this.touchEndX > sensitivity) {
          this.navigateToSlide(this.currentSlideIndex + 1, 'next'); // Swipe left, move to next slide
        } else if (this.touchEndX - this.touchStartX > sensitivity) {
          this.navigateToSlide(this.currentSlideIndex - 1, 'prev'); // Swipe right, move to previous slide
        }
      }
    };

    this.carousel.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.carousel.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.carousel.addEventListener('touchend', this.handleTouchEnd, { passive: true });
  }

  updateCarousel(direction) {
    const transitionDuration = this.transitionDuration; // Use the transition duration set in the slider
    
    // Use the direction to calculate the previous item index correctly
    let previousSlideIndex;
    if (direction === 'next') {
      previousSlideIndex = this.currentSlideIndex - 1 < 1 ? this.numItems : this.currentSlideIndex - 1;
    } else if (direction === 'prev') {
      previousSlideIndex = this.currentSlideIndex + 1 > this.numItems ? 1 : this.currentSlideIndex + 1;
    }

    this.carousel.style.setProperty('--duration', `${transitionDuration}ms`);
    this.carousel.style.setProperty('--interval', `${this.autoplayIntervalDuration}ms`);

    // Clear any previous timeouts before proceeding
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
      this.activeTimeout = null; // Reset the timeout tracker
    }
  
    // Reset classes and handle transitions
    this.carouselItems.forEach(item => {
      item.classList.remove('animate-out', 'animate-in'); // Reset animation classes
    });
  
    // Animate out the previous item
    const previousItem = this.carouselItems[previousSlideIndex - 1];
    if (previousItem) {
      previousItem.classList.add('animate-out');
      setTimeout(() => {
        previousItem.classList.remove('animate-out', 'active');
        const prevVideo = previousItem.querySelector('video');
        if (prevVideo) {
          prevVideo.pause();
          prevVideo.currentTime = 0; // Reset video
        }
      }, transitionDuration);
    }

    // Animate in the current item
    const currentItem = this.carouselItems[this.currentSlideIndex - 1];
    if (currentItem) {
      const currentVideo = currentItem.querySelector('video');
      if (currentVideo) {
        this.autoplayIntervalDuration = Math.ceil(currentVideo.duration * 1000);
        currentVideo.play();
      } else {
        this.autoplayIntervalDuration = this.autoplayStandardIntervalDuration;
      }
      currentItem.classList.add('active');
      this.activeTimeout = setTimeout(() => {
        currentItem.classList.add('animate-in');
      }, transitionDuration);
    }

    // Update other elements like navigation arrows and progress bar
    this.updateNavigationArrows();
    this.updateProgressBar();
    this.dispatchCarouselUpdateEvent();
  }

  updateNavigationArrows() {
    if (this.prevButton) {
      if (!this.infiniteLoop && this.currentSlideIndex === 1) {
        this.prevButton.classList.add('disabled');
      } else {
        this.prevButton.classList.remove('disabled');
      }
    }
  
    if (this.nextButton) {
      if (!this.infiniteLoop && this.currentSlideIndex === this.numItems) {
        this.nextButton.classList.add('disabled');
      } else {
        this.nextButton.classList.remove('disabled');
      }
    }
  }

  updateProgressBar() {
    if (this.progressBar) {
      const progressPercentage = (this.currentSlideIndex / this.numItems) * 100;
      this.progressBar.style.width = `${progressPercentage}%`;
    }
  }

  navigateToSlide(index, direction) {
    // Handle infinite loop
    if (index > this.numItems) {
      this.currentSlideIndex = 1;
    } else if (index < 1) {
      this.currentSlideIndex = this.numItems;
    } else {
      this.currentSlideIndex = index;
    }
  
    this.updateCarousel(direction); // Pass the direction to updateCarousel
    this.resetAutoplay();
  }
  
  dispatchCarouselUpdateEvent() {
    const event = new CustomEvent('carouselUpdate', {
      detail: { index: this.currentSlideIndex, totalItems: this.numItems, visibleItems: this.numVisibleItems },
      bubbles: true // This allows the event to bubble up through the DOM
    });
    this.carousel.dispatchEvent(event);
  }

  resetAutoplay() {
    if (this.autoplay && this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.resetTimerBar(); // Reset the timer when autoplay resets
      this.setupAutoplay();
    }
  }

  resetTimerBar() {
    if (this.progressBar) {
      this.progressBar.style.transition = 'none'; // Disable transition for immediate reset
      this.progressBar.style.width = '0%'; // Reset to 0%
      // Force reflow to ensure the width resets immediately
      this.progressBar.offsetHeight; 
      this.progressBar.style.transition = `width ${this.autoplayIntervalDuration}ms linear`; // Reapply transition for smooth progress
    }
  }
}

// Create a global object to store slider instances
window.sliderInstances = {};

// Initializes all carousels on page load
window.addEventListener('pageshow', () => {
  const heroCarousels = document.querySelectorAll('.hero-carousel__slider');
  heroCarousels.forEach((carousel, index) => {
    carousel.setAttribute('data-carousel-index', index);
    const sliderInstance = new Slider('hero-carousel', index, true, 500, 6000);

    // Store the instance in a global object, using the index as the key
    window.sliderInstances[`hero-carousel-${index}`] = sliderInstance;
  });
});
